import React, { useEffect, useMemo, useRef, useState } from "react";
import { useIncomesInRange, useDeleteIncome, useCreateIncome, useUpdateIncome } from "../../hooks/income";
import { useCategoriesQuery } from "../../hooks/category";
import { useSearchParams } from "react-router-dom";
import {
  alpha,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Add as AddIcon,
  ArrowDownward as ArrowDownwardIcon,
  ArrowUpward as ArrowUpwardIcon,
  WarningAmberRounded as WarningAmberRoundedIcon,
  Category as CategoryIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  FilterList as FilterListIcon,
  AccountBalance as IncomeIcon,
  Search as SearchIcon,
  Sort as SortIcon,
} from "@mui/icons-material";
import Income from "../../models/finance/Income";
import {
  getFirstDayOfMonth,
  getLastDayOfMonth,
  getMonthIndex,
} from "../../utils/DateUtil";
import IncomeFormDrawer from "../finance/IncomeFormDrawer";
import { CategoryType } from "../../models/finance/CategoryType";
import { useCurrencyFormatter } from "../../contexts/CurrencyContext";
import PrimaryButton from "../common/ui/PrimaryButton";
import { useError } from "../../error/useError";
import ErrorAlert from "../common/ErrorAlert";
import { useUserProfileContext } from "../../contexts/ProfileContext";

interface IncomeTableComponentProps {
  year: number;
  month: string;
  onIncomeChange?: () => void;
  addEntryTrigger?: number;
}

const IncomeTableComponent: React.FC<IncomeTableComponentProps> = ({
  year,
  month,
  onIncomeChange,
  addEntryTrigger,
}) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const { formatCurrencyNumber: formatCurrency } = useCurrencyFormatter();
  const { formatDate } = useUserProfileContext();
  const { error, helpText, clearError } = useError();
  const accent = theme.palette.success.main;
  const accentSoft = alpha(theme.palette.success.main, 0.22);
  const accentMuted = alpha(theme.palette.success.main, 0.12);
  const incomePrimaryActionSx = {
    background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
    color: theme.palette.success.contrastText,
    boxShadow: "0 8px 24px rgba(22, 163, 74, 0.28)",
    "&:hover": {
      background: `linear-gradient(135deg, ${theme.palette.success.dark}, ${theme.palette.success.dark})`,
      boxShadow: "0 12px 34px rgba(22, 163, 74, 0.34)",
    },
  };

  const startDate = getFirstDayOfMonth(year, getMonthIndex(month));
  const endDate = getLastDayOfMonth(year, getMonthIndex(month));

  const { data: incomes = [], isLoading: loading, refetch: refetchIncomes } = useIncomesInRange(startDate, endDate);
  const { data: categories = [] } = useCategoriesQuery(CategoryType.Income, true);
  const { mutate: deleteIncome, isPending: isDeletingIncome } = useDeleteIncome();
  const { mutate: createIncome } = useCreateIncome();
  const { mutate: updateIncome } = useUpdateIncome();

  const [searchParams, setSearchParams] = useSearchParams();
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">(
    (searchParams.get("sortOrder") as "asc" | "desc") || "desc"
  );
  const [sortBy, setSortBy] = useState<"date" | "amount" | "category">(
    (searchParams.get("sortBy") as "date" | "amount" | "category") || "date"
  );
  const [selectedCategory, setSelectedCategory] = useState<string>(
    searchParams.get("category") || ""
  );
  const [searchText, setSearchText] = useState(searchParams.get("query") || "");
  const [amountBand, setAmountBand] = useState<"all" | "aboveAvg" | "topQuartile">(
    (searchParams.get("amountBand") as "all" | "aboveAvg" | "topQuartile") || "all"
  );

  const [categoryMenuAnchor, setCategoryMenuAnchor] = useState<null | HTMLElement>(null);

  const [page, setPage] = useState(0);
  const rowsPerPage = 10;

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<"add" | "edit">("add");
  const [selectedIncome, setSelectedIncome] = useState<Income | undefined>(undefined);
  const [deleteCandidate, setDeleteCandidate] = useState<Income | undefined>(undefined);
  const [deleteOrigin, setDeleteOrigin] = useState<"row" | "drawer">("row");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const lastAddTriggerRef = useRef<number | undefined>(addEntryTrigger);

  const updateSearchParams = (newParams: Record<string, string>) => {
    const updatedParams = new URLSearchParams(searchParams.toString());
    Object.entries(newParams).forEach(([key, value]) => {
      if (value) {
        updatedParams.set(key, value);
      } else {
        updatedParams.delete(key);
      }
    });
    setSearchParams(updatedParams);
  };

  const setSort = (property: "date" | "amount" | "category") => {
    const newOrder =
      sortBy === property
        ? sortOrder === "asc"
          ? "desc"
          : "asc"
        : property === "category"
          ? "asc"
          : "desc";

    setSortBy(property);
    setSortOrder(newOrder);
    updateSearchParams({ sortBy: property, sortOrder: newOrder });
  };

  const toggleSortOrder = () => {
    const next = sortOrder === "asc" ? "desc" : "asc";
    setSortOrder(next);
    updateSearchParams({ sortBy, sortOrder: next });
  };

  const handleCategoryFilter = (categoryTitle: string) => {
    setSelectedCategory(categoryTitle);
    setCategoryMenuAnchor(null);
    updateSearchParams({ category: categoryTitle });
  };

  const clearCategoryFilter = () => {
    setSelectedCategory("");
    setCategoryMenuAnchor(null);
    updateSearchParams({ category: "" });
  };

  useEffect(() => {
    setPage(0);
  }, [selectedCategory, searchText, sortBy, sortOrder]);

  const filteredIncomes = useMemo(() => {
    const normalizedQuery = searchText.trim().toLowerCase();
    const average = incomes.length > 0 ? incomes.reduce((sum, i) => sum + i.amount, 0) / incomes.length : 0;
    const sortedAmounts = incomes.map((i) => i.amount).sort((a, b) => a - b);
    const quartileIndex = Math.floor(sortedAmounts.length * 0.75);
    const topQuartileThreshold = sortedAmounts[quartileIndex] ?? 0;

    return incomes.filter((income) => {
      const matchesCategory = selectedCategory ? income.category === selectedCategory : true;

      const matchesText = normalizedQuery
        ? income.description.toLowerCase().includes(normalizedQuery) || income.category.toLowerCase().includes(normalizedQuery)
        : true;

      const matchesAmountBand =
        amountBand === "all"
          ? true
          : amountBand === "aboveAvg"
            ? income.amount >= average && average > 0
            : income.amount >= topQuartileThreshold && topQuartileThreshold > 0;

      return matchesCategory && matchesText && matchesAmountBand;
    });
  }, [incomes, selectedCategory, searchText, amountBand]);

  const sortedIncomes = useMemo(() => {
    return [...filteredIncomes].sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case "date":
          comparison = new Date(a.date).getTime() - new Date(b.date).getTime();
          break;
        case "amount":
          comparison = a.amount - b.amount;
          break;
        case "category":
          comparison = a.category.localeCompare(b.category);
          break;
      }

      return sortOrder === "asc" ? comparison : -comparison;
    });
  }, [filteredIncomes, sortBy, sortOrder]);

  const paginatedIncomes = sortedIncomes.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const totalAmount = filteredIncomes.reduce((sum, income) => sum + income.amount, 0);
  const averageAmount = filteredIncomes.length > 0 ? totalAmount / filteredIncomes.length : 0;

  const categoryIncome = useMemo(() => {
    const map = filteredIncomes.reduce<Record<string, number>>((acc, income) => {
      acc[income.category] = (acc[income.category] || 0) + income.amount;
      return acc;
    }, {});

    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4);
  }, [filteredIncomes]);

  const handleEditIncome = (income: Income) => {
    setSelectedIncome(income);
    setDrawerMode("edit");
    setDrawerOpen(true);
  };

  const handleAddIncome = () => {
    setSelectedIncome(undefined);
    setDrawerMode("add");
    setDrawerOpen(true);
  };

  useEffect(() => {
    if (addEntryTrigger === undefined) {
      return;
    }

    if (lastAddTriggerRef.current === addEntryTrigger) {
      return;
    }

    lastAddTriggerRef.current = addEntryTrigger;
    handleAddIncome();
  }, [addEntryTrigger]);

  const handleSaveIncome = (
    id: string,
    date: Date,
    amount: number,
    description: string,
    categoryTitle: string
  ) => {
    if (drawerMode === "add") {
      createIncome(
        { date, amount, description, categoryTitle },
        {
          onSuccess: () => {
            setDrawerOpen(false);
            onIncomeChange?.();
          },
        }
      );
      return;
    }

    updateIncome(
      { id, date, amount, description, categoryTitle },
      {
        onSuccess: () => {
          setDrawerOpen(false);
          onIncomeChange?.();
        },
      }
    );
  };


  const handleSaveAndAddAnother = (
    date: Date,
    amount: number,
    description: string,
    categoryTitle: string,
    onSuccessCallback?: () => void
  ) => {
    if (drawerMode !== "add") {
      return;
    }

    createIncome(
      { date, amount, description, categoryTitle },
      {
        onSuccess: () => {
          setDrawerOpen(true);
          onSuccessCallback?.();
        },
      }
    );
  };

  const handleRequestDeleteIncome = (income: Income, origin: "row" | "drawer" = "row") => {
    setDeleteCandidate(income);
    setDeleteOrigin(origin);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDeleteIncome = () => {
    if (!deleteCandidate) {
      return;
    }

    deleteIncome(
      { id: deleteCandidate.id },
      {
        onSuccess: () => {
          setDeleteDialogOpen(false);
          setDeleteCandidate(undefined);
          setDrawerOpen(false);
          onIncomeChange?.();
        },
      }
    );
  };

  const handleCancelDeleteDialog = () => {
    if (isDeletingIncome) {
      return;
    }
    setDeleteDialogOpen(false);
    setDeleteCandidate(undefined);
  };

  const handleExportCsv = () => {
    const escapeCsv = (value: string | number) => `"${String(value).replace(/"/g, '""')}"`;

    const rows = sortedIncomes.map((income) => [
      formatDate(income.date),
      income.description,
      income.category,
      income.amount,
    ]);

    const csv = [["Date", "Description", "Category", "Amount"], ...rows]
      .map((row) => row.map(escapeCsv).join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `incomes-${month}-${year}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Skeleton variant="rounded" height={84} sx={{ mb: 1.5 }} />
        <Skeleton variant="rounded" height={56} sx={{ mb: 1.5 }} />
        {[...Array(4)].map((_, idx) => (
          <Skeleton key={idx} variant="rounded" height={74} sx={{ mb: 1 }} />
        ))}
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <ErrorAlert
          error={error}
          helpText={helpText}
          onClear={clearError}
          onRetry={() => refetchIncomes()}
          title="Income Table Error"
        />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          p: { xs: 2, md: 3 },
          borderBottom: `1px solid ${theme.palette.divider}`,
          borderTopLeftRadius: "18px",
          borderTopRightRadius: "18px",
          background: `linear-gradient(165deg, ${accentMuted}, transparent)`,
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, fontFamily: '"Space Grotesk", "Manrope", sans-serif', mb: 0.5 }}
            >
              Income Transactions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {month} {year} ledger view with quick search, filters, and sorting.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Showing {sortedIncomes.length} of {incomes.length} transactions
            </Typography>
          </Box>

          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportCsv}
              sx={{
                borderRadius: "10px",
                borderColor: alpha(accent, 0.45),
                color: theme.palette.success.light,
                textTransform: "none",
                "&:hover": {
                  borderColor: accent,
                  backgroundColor: accentMuted,
                },
              }}
            >
              Export CSV
            </Button>
            <PrimaryButton startIcon={<AddIcon />} onClick={handleAddIncome} sx={incomePrimaryActionSx}>
              Add Income
            </PrimaryButton>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
          <Chip icon={<IncomeIcon />} label={`${filteredIncomes.length} records`} sx={{ borderRadius: "10px" }} />
          <Chip
            label={`Total ${formatCurrency(totalAmount)}`}
            sx={{
              borderRadius: "10px",
              backgroundColor: alpha(theme.palette.success.main, 0.18),
              color: theme.palette.success.light,
              fontWeight: 700,
            }}
          />
          <Chip
            label={`Average ${formatCurrency(averageAmount)}`}
            sx={{
              borderRadius: "10px",
              backgroundColor: accentSoft,
              color: theme.palette.success.light,
            }}
          />
        </Stack>

        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mb: 1.25 }}>
          <Chip
            label="All Amounts"
            onClick={() => {
              setAmountBand("all");
              updateSearchParams({ amountBand: "all" });
            }}
            variant={amountBand === "all" ? "filled" : "outlined"}
            sx={{ borderRadius: "10px" }}
          />
          <Chip
            label="Above Average"
            onClick={() => {
              setAmountBand("aboveAvg");
              updateSearchParams({ amountBand: "aboveAvg" });
            }}
            variant={amountBand === "aboveAvg" ? "filled" : "outlined"}
            sx={{ borderRadius: "10px" }}
          />
          <Chip
            label="Top Quartile"
            onClick={() => {
              setAmountBand("topQuartile");
              updateSearchParams({ amountBand: "topQuartile" });
            }}
            variant={amountBand === "topQuartile" ? "filled" : "outlined"}
            sx={{ borderRadius: "10px" }}
          />

          {(searchText || selectedCategory || amountBand !== "all") && (
            <Button
              size="small"
              variant="text"
              onClick={() => {
                setSearchText("");
                setSelectedCategory("");
                setAmountBand("all");
                updateSearchParams({ query: "", category: "", amountBand: "all" });
              }}
              sx={{ textTransform: "none", borderRadius: "10px", color: theme.palette.text.secondary }}
            >
              Clear all controls
            </Button>
          )}
        </Stack>

        <Stack direction={{ xs: "column", lg: "row" }} spacing={1.25} sx={{ mb: 1.25 }}>
          <TextField
            value={searchText}
            onChange={(event) => {
              const value = event.target.value;
              setSearchText(value);
              updateSearchParams({ query: value });
            }}
            size="small"
            fullWidth
            placeholder="Search description or category"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={(event) => setCategoryMenuAnchor(event.currentTarget)}
            sx={{
              borderRadius: "10px",
              borderColor: alpha(accent, 0.55),
              color: theme.palette.success.light,
              minWidth: { lg: 230 },
              whiteSpace: "nowrap",
              "&:hover": {
                borderColor: accent,
                backgroundColor: accentMuted,
              },
            }}
          >
            {selectedCategory ? `Category: ${selectedCategory}` : "Filter by Category"}
          </Button>
        </Stack>

        <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ xs: "stretch", md: "center" }}>
          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
            {(["date", "amount", "category"] as const).map((field) => (
              <Chip
                key={field}
                onClick={() => setSort(field)}
                icon={<SortIcon sx={{ fontSize: 16 }} />}
                label={`Sort ${field}`}
                variant={sortBy === field ? "filled" : "outlined"}
                sx={{
                  borderRadius: "10px",
                  textTransform: "capitalize",
                  borderColor: alpha(accent, 0.5),
                  backgroundColor: sortBy === field ? accentSoft : "transparent",
                }}
              />
            ))}
          </Stack>

          <Button
            variant="text"
            onClick={toggleSortOrder}
            startIcon={sortOrder === "asc" ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
            sx={{
              color: theme.palette.text.secondary,
              borderRadius: "10px",
              textTransform: "none",
              alignSelf: { xs: "flex-start", md: "center" },
            }}
          >
            {sortOrder === "asc" ? "Ascending" : "Descending"}
          </Button>
        </Stack>

        {categoryIncome.length > 0 && (
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.25 }}>
            {categoryIncome.map(([category, amount]) => (
              <Chip
                key={category}
                onClick={() => handleCategoryFilter(category)}
                icon={<CategoryIcon />}
                label={`${category} ${formatCurrency(amount)}`}
                variant={selectedCategory === category ? "filled" : "outlined"}
                sx={{
                  borderRadius: "10px",
                  maxWidth: "100%",
                  borderColor: alpha(accent, 0.35),
                  backgroundColor: selectedCategory === category ? accentSoft : "transparent",
                }}
              />
            ))}
          </Stack>
        )}
      </Box>

      {filteredIncomes.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 8, px: 2 }}>
          <Avatar
            sx={{
              width: 64,
              height: 64,
              mx: "auto",
              mb: 2,
              bgcolor: alpha(theme.palette.success.main, 0.2),
              color: theme.palette.success.light,
            }}
          >
            <IncomeIcon />
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            No matching incomes
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            {selectedCategory || searchText
              ? "Adjust search or filters to reveal transactions."
              : `No incomes have been recorded for ${month} ${year}.`}
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="center">
            <PrimaryButton startIcon={<AddIcon />} onClick={handleAddIncome} sx={incomePrimaryActionSx}>
              Add First Income
            </PrimaryButton>
            {(selectedCategory || searchText) && (
              <Button
                variant="outlined"
                onClick={() => {
                  setSearchText("");
                  clearCategoryFilter();
                  updateSearchParams({ query: "" });
                }}
                sx={{
                  borderRadius: "10px",
                  borderColor: alpha(accent, 0.5),
                  color: theme.palette.success.light,
                }}
              >
                Clear Filters
              </Button>
            )}
          </Stack>
        </Box>
      ) : (
        <>
          <Box sx={{ px: { xs: 1.5, md: 2.5 }, py: { xs: 1, md: 1.5 }, pb: { xs: 4, md: 1.5 } }}>
            {!isSmall && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "140px 1fr 170px 150px 110px",
                  gap: 1,
                  px: 1,
                  pb: 0.75,
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                  Date
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                  Description
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                  Category
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textAlign: "right" }}>
                  Amount
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textAlign: "center" }}>
                  Actions
                </Typography>
              </Box>
            )}

            <Stack spacing={1}>
              {paginatedIncomes.map((income) => (
                <Box
                  key={income.id}
                  sx={{
                    p: { xs: 1.5, md: 1.25 },
                    borderRadius: "12px",
                    border: `1px solid ${alpha(theme.palette.text.primary, 0.07)}`,
                    backgroundColor: alpha(theme.palette.background.paper, 0.5),
                    transition: "all .2s ease",
                    "&:hover": {
                      borderColor: alpha(accent, 0.45),
                      backgroundColor: accentMuted,
                    },
                  }}
                >
                  {isSmall ? (
                    <Stack spacing={1}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(income.date)}
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: theme.palette.success.light, fontWeight: 700 }}>
                          {formatCurrency(income.amount)}
                        </Typography>
                      </Stack>

                      <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                        {income.description}
                      </Typography>

                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Chip size="small" label={income.category} variant="outlined" sx={{ borderRadius: 2 }} />
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title="Edit income">
                            <IconButton size="small" onClick={() => handleEditIncome(income)} sx={{ color: theme.palette.info.main }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete income">
                            <IconButton size="small" onClick={() => handleRequestDeleteIncome(income, "row")} sx={{ color: theme.palette.error.main }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Stack>
                    </Stack>
                  ) : (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "140px 1fr 170px 150px 110px",
                        gap: 1,
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatDate(income.date)}
                      </Typography>

                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {income.description}
                      </Typography>

                      <Chip
                        size="small"
                        label={income.category}
                        variant="outlined"
                        sx={{ width: "fit-content", borderRadius: 2 }}
                      />

                      <Typography
                        variant="body2"
                        sx={{ textAlign: "right", color: theme.palette.success.light, fontWeight: 700 }}
                      >
                        {formatCurrency(income.amount)}
                      </Typography>

                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="Edit income">
                          <IconButton size="small" onClick={() => handleEditIncome(income)} sx={{ color: theme.palette.info.main }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete income">
                          <IconButton size="small" onClick={() => handleRequestDeleteIncome(income, "row")} sx={{ color: theme.palette.error.main }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Box>
                  )}
                </Box>
              ))}
            </Stack>
          </Box>

          <TablePagination
            component="div"
            count={sortedIncomes.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10]}
            sx={{
              borderTop: `1px solid ${theme.palette.divider}`,
              px: { xs: 1, md: 2 },
            }}
          />
        </>
      )}

      <Menu
        anchorEl={categoryMenuAnchor}
        open={Boolean(categoryMenuAnchor)}
        onClose={() => setCategoryMenuAnchor(null)}
        PaperProps={{ sx: { maxHeight: 320, minWidth: 220 } }}
      >
        <MenuItem onClick={clearCategoryFilter}>
          <Typography sx={{ fontStyle: "italic" }}>All Categories</Typography>
        </MenuItem>
        {categories.map((category) => (
          <MenuItem
            key={category.id}
            onClick={() => handleCategoryFilter(category.title)}
            selected={selectedCategory === category.title}
          >
            {category.title}
          </MenuItem>
        ))}
      </Menu>

      <IncomeFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSave={handleSaveIncome}
        onSaveAndAdd={handleSaveAndAddAnother}
        onDelete={(income) => handleRequestDeleteIncome(income, "drawer")}
        mode={drawerMode}
        initialData={selectedIncome}
        selectedYear={year}
        selectedMonth={getMonthIndex(month)}
        categories={categories}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDeleteDialog}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.error.main, 0.25)}`,
            background: `linear-gradient(165deg, ${alpha(theme.palette.background.paper, 0.98)}, ${alpha(theme.palette.background.default, 0.98)})`,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Avatar
              sx={{
                width: 34,
                height: 34,
                bgcolor: alpha(theme.palette.error.main, 0.2),
                color: theme.palette.error.light,
              }}
            >
              <WarningAmberRoundedIcon fontSize="small" />
            </Avatar>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Delete income?
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {deleteOrigin === "drawer" ? "You are deleting from the edit panel." : "You are deleting from the transactions list."}
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
            This action cannot be undone. The selected income entry will be permanently removed.
          </Typography>
          {deleteCandidate && (
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
              <Chip size="small" label={formatDate(deleteCandidate.date)} />
              <Chip size="small" label={deleteCandidate.category} />
              <Chip
                size="small"
                label={formatCurrency(deleteCandidate.amount)}
                sx={{
                  bgcolor: alpha(theme.palette.success.main, 0.18),
                  color: theme.palette.success.light,
                }}
              />
            </Stack>
          )}
          {deleteCandidate && (
            <Typography variant="body2" sx={{ mt: 1.5, fontWeight: 600 }}>
              {deleteCandidate.description}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.25, pt: 0.25 }}>
          <Button
            onClick={handleCancelDeleteDialog}
            disabled={isDeletingIncome}
            sx={{ textTransform: "none" }}
          >
            Keep income
          </Button>
          <Button
            onClick={handleConfirmDeleteIncome}
            color="error"
            variant="contained"
            disabled={isDeletingIncome || !deleteCandidate}
            sx={{ textTransform: "none", borderRadius: "10px" }}
          >
            {isDeletingIncome ? "Deleting..." : "Delete permanently"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IncomeTableComponent;
